// One BOARD CARD (brief §5.2): subject display, the figure + its note, tags (≤3 + overflow), the
// needs-a-decision / attention mini badge, and the evidence as-of stamp — and NO verdict control
// (the one-primary-button rule; verdicts live inside the opened card, brief §5.2). Every rendered
// state derives from the row the projection carried (G-182); opening routes the item id to the
// container's callback. The ghost variant renders a recently-exited row (dashed, quiet).

import type { WorkItemRow } from '../../projections.ts';
import { WorkMiniBadge, WorkTag } from '../../work-ui.tsx';

const MAX_TAGS = 3;

function CardMeta({ item }: { readonly item: WorkItemRow }): React.JSX.Element {
  const shown = item.tags.slice(0, MAX_TAGS);
  const overflow = item.tags.length - shown.length;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      {shown.map((tag) => (
        <WorkTag key={tag} label={tag} />
      ))}
      {overflow > 0 ? (
        <span data-tag-overflow={overflow} className="ritona-numeric text-xs text-text-subtle">
          +{overflow}
        </span>
      ) : null}
      {item.needsDecision ? (
        <WorkMiniBadge kind="decide">needs a decision</WorkMiniBadge>
      ) : item.attentionLabel !== null ? (
        <WorkMiniBadge kind="stop">{item.attentionLabel}</WorkMiniBadge>
      ) : null}
      <span className="ritona-numeric ml-auto text-xs text-text-subtle">
        evidence {item.evidenceAsOf}
      </span>
    </div>
  );
}

export interface BoardItemCardProps {
  readonly item: WorkItemRow;
  readonly onOpen: (itemId: string) => void;
  readonly ghost?: boolean;
}

/** One clickable board card — a signal, never a control surface. */
export function BoardItemCard({
  item,
  onOpen,
  ghost = false,
}: BoardItemCardProps): React.JSX.Element {
  const attention = item.needsDecision || item.attentionLabel !== null;
  const border = ghost
    ? 'border-dashed border-border opacity-75'
    : attention
      ? 'border-bracken'
      : 'border-border';
  return (
    <button
      type="button"
      data-item={item.id}
      data-item-state={item.state}
      onClick={() => onOpen(item.id)}
      aria-label={`Open ${item.subjectDisplay}`}
      className={`w-full rounded-md border bg-surface px-3 py-2.5 text-left transition-colors hover:border-border-strong ${border}`}
    >
      <div className="text-sm font-medium text-text">{item.subjectDisplay}</div>
      {item.figure !== null ? (
        <div className="ritona-voice mt-1 flex items-baseline gap-2 text-base text-text">
          {item.figure}
          {item.figureNote !== null ? (
            <span className="ritona-numeric text-xs font-normal text-text-subtle">
              {item.figureNote}
            </span>
          ) : null}
        </div>
      ) : item.figureNote !== null ? (
        <div className="ritona-numeric mt-1 text-xs text-text-subtle">{item.figureNote}</div>
      ) : null}
      <CardMeta item={item} />
    </button>
  );
}
